import React, { useState } from 'react';
import {
  IconButton,
  Modal,
  RadioGroup,
  Typography,
  Radio,
  styled,
  FormControlLabel,
  Drawer,
  FormControl,
  Grid,
} from '@mui/material';
import { Box } from '@mui/system';
import { Icon } from '@iconify/react';
import { ModalBody } from '../../components';
import CustomerAssignment from './CustomerAssignment';
import InternalAssignment from './InternalAssignment';
import { ICompSummaryBase } from '../../types';
import AssignmentWithoutOrder from './AssignmentWithOutOrder';
import { useAppSelector } from '../../hooks/useRedux';

type Props = {
  show: boolean;
  onHide: () => void;
  toCompareVehicle: ICompSummaryBase & { [key: string]: string };
  updateInventory: () => void;
};

type ToggleVal = 'with_order' | 'without_order';

const RadioBtn = styled(Radio)(() => ({
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
}));

const AssignmentModal: React.FC<Props> = ({ onHide, show, toCompareVehicle, updateInventory }) => {
  const [value, setValue] = useState<ToggleVal>('with_order');
  const [toggle, setToggle] = useState(true);
  const toggleCustomerTypeSelection = (flagValue: boolean) => {
    setToggle(flagValue);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue((event.target as HTMLInputElement).value as ToggleVal);
  };

  const closeModal = (_: any, reason: string) => {
    toggleCustomerTypeSelection(true);
    if (reason !== 'escapeKeyDown' && reason !== 'backdropClick') {
      updateInventory();
    }
    onHide();
  };

  const img = useAppSelector((state) => {
    if (toCompareVehicle) {
      const formatUrl = toCompareVehicle?.imageUrl;
      if (formatUrl) {
        const url = new URL(formatUrl);
        const { searchParams } = url;

        url.searchParams.set('width', '630');
        url.searchParams.set('height', '472.5');
        url.searchParams.set('format', 'png');
        url.searchParams.set('camera', 'Angle_EXT_10_0010');
        url.searchParams.set('background_colour', '00000000');
        url.searchParams.set('env', 'lucid_studio');

        // change the search property of the main url
        url.search = searchParams.toString();

        // the new url string
        const newUrl = url.toString();

        return newUrl;
      }
      return toCompareVehicle?.imageUrl;
    }
    return null;
  });

  return (
    <Drawer
      open={show}
      onClose={closeModal}
      anchor="right"
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ height: '100%', py: 4, px: 3, bgcolor: '#fafafa' }} width="80vw">
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography sx={{ fontSize: '24px', fontWeight: '600' }}>Available Vehicle</Typography>
          <IconButton onClick={() => closeModal(null, 'appClose')} sx={{ marginRight: '-10px' }}>
            <Icon icon="ei:close" />
          </IconButton>
        </Box>
        <Box
          sx={{
            padding: (theme) => theme.spacing(3, 3),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '92%',
            backgroundColor: '#fff',
            border: '1px solid #ececec',
            borderRadius: 2,
          }}
        >
          {toggle && (
            <Box display="flex">
              <Box>
                <Box display="flex" alignItems="center">
                  <Typography id="modal-modal-title" variant="h6" component="h2">
                    Request assignment or hold vehicle
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 200 }}>
                  Assignment request might take a few days for approval. Please confirm to submit request.
                </Typography>
                <Box>
                  <FormControl>
                    <RadioGroup
                      row
                      aria-labelledby="radio-buttons-group"
                      name="row-radio-buttons-group"
                      sx={{
                        gap: 2.5,
                        ' .MuiTypography-root': {
                          fontSize: 14,
                        },
                        ' svg': {
                          color: (theme) => theme.palette.common.black,
                        },
                      }}
                      value={value}
                      onChange={(e) => setValue(e.target.value as ToggleVal)}
                    >
                      <FormControlLabel
                        value="with_order"
                        control={<Radio size="small" />}
                        label="Customer with order"
                      />
                      <FormControlLabel
                        value="without_order"
                        control={<Radio size="small" />}
                        label="Customer without order"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>
              {toCompareVehicle && (
                <Box paddingLeft="30px" display="inline-flex">
                  {img && (
                    <Box>
                      <img src={img} alt="Inventory Vehicle Selected" width="125px" />
                    </Box>
                  )}
                  <Box>
                    <Grid alignSelf="center">
                      <Box display="flex" alignItems="center">
                        <Typography fontWeight="Bold">Inventory Vehicle Selected</Typography>
                      </Box>
                      <Typography variant="caption" fontSize="1rem">
                        {toCompareVehicle.year}, {toCompareVehicle.model}, {toCompareVehicle.exteriorColor},{' '}
                        {toCompareVehicle.interiorColor}, {toCompareVehicle.roof}, {toCompareVehicle.wheels}
                      </Typography>
                      {toCompareVehicle.currentState && toCompareVehicle.currentCity && (
                        <Typography variant="caption" fontSize="1rem">
                          {'\n'}Currently in {toCompareVehicle.currentCity}, {toCompareVehicle.currentState}
                        </Typography>
                      )}
                    </Grid>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {value === 'with_order' && (
            <CustomerAssignment
              newToggle={toggleCustomerTypeSelection}
              toCompareVehicle={toCompareVehicle}
              closeModal={() => closeModal(null, 'appClose')}
            />
          )}
          {value === 'without_order' && (
            <AssignmentWithoutOrder
              newToggle={toggleCustomerTypeSelection}
              toCompareVehicle={toCompareVehicle}
              closeModal={() => closeModal(null, 'appClose')}
            />
          )}
          {/* {value === 'internal' && <InternalAssignment />} */}
        </Box>
      </Box>
    </Drawer>
  );
};

export default AssignmentModal;
